import { useEffect, useState } from 'react';
import {
  apiInvestigados,
  apiPesquisasRegistradas,
  type Investigado,
  type PesquisaRegistrada,
  type PesquisaRegistravel,
} from '../lib/api';
import { usarEhMaster } from '../lib/papel.ts';
import { Confirmar } from './Confirmar.tsx';
import { Traco } from './ImoveisDoCaso';
import '../estilos/imoveis-2026.css';

/**
 * ⭐⭐ **"REGISTREI ESTA PESQUISA" — decisão 14 do Richard, 13/09/2026.**
 *
 * Quatro pesquisas acontecem fora do Hub, feitas por uma pessoa: cartórios de
 * registro de imóveis por titular, indisponibilidade de bens, juntas comerciais
 * e marcas. ⛔ **O relatório não tinha como saber delas** — e o capítulo vazio
 * parecia dizer que ninguém olhou.
 *
 * ⭐ **O registro é o que faz o escopo citar a pesquisa e a ressalva sair no
 * capítulo.** Por parte, e dizendo o resultado: "nada consta" é entrega, e
 * "achei" impede o material dos prompts de escrever que nada foi localizado.
 *
 * ⚠️ **Registrar de novo substitui** — o clique errado se desfaz clicando o
 * certo. "Apagar registro" é para a pesquisa que não aconteceu.
 *
 * ⚠️ **Só o master registra**, como na central notarial: o registro põe texto no
 * documento do cliente. O colaborador vê o que foi registrado.
 */
const DA_PESQUISA: Record<PesquisaRegistravel, { titulo: string; rotuloCurto: string; achou: string }> = {
  imoveis_por_titular: {
    titulo: 'Imóveis por titular, nos cartórios de registro de imóveis',
    rotuloCurto: 'Imóveis por titular',
    achou: 'Achou imóvel',
  },
  indisponibilidade_de_bens: {
    titulo: 'Ordens de indisponibilidade de bens',
    rotuloCurto: 'Indisponibilidade',
    achou: 'Achou ordem',
  },
  junta_comercial: {
    titulo: 'Empresas por titular, nas juntas comerciais',
    rotuloCurto: 'Juntas comerciais',
    achou: 'Achou empresa',
  },
  marcas: {
    titulo: 'Marcas por titular',
    rotuloCurto: 'Marcas',
    achou: 'Achou marca',
  },
  /* ⭐ 16/10/2026 (PRD §52.9): sem consulta automática pelo dono, a pesquisa se registra. */
  embarcacoes: {
    titulo: 'Embarcações',
    rotuloCurto: 'Embarcações',
    achou: 'Achou embarcação',
  },
};

/** Data de São Paulo, só o dia. */
function dia(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

export function PesquisaFeitaFora({
  casoId,
  servico,
  aoMudar,
}: {
  casoId: string;
  servico: PesquisaRegistravel;
  /** Avisa a página para recarregar a ressalva do capítulo. */
  aoMudar?: () => void;
}) {
  const ehMaster = usarEhMaster();
  const [partes, setPartes] = useState<Investigado[] | null>(null);
  const [registradas, setRegistradas] = useState<PesquisaRegistrada[]>([]);
  const [indo, setIndo] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aberto, setAberto] = useState(false);
  const [aApagar, setAApagar] = useState<{ registro: PesquisaRegistrada; nome: string } | null>(null);

  useEffect(() => {
    let vivo = true;
    void Promise.all([apiInvestigados.listar(casoId), apiPesquisasRegistradas.doCaso(casoId)])
      .then(([i, r]) => {
        if (!vivo) return;
        setPartes(i.investigados);
        setRegistradas(r.registradas);
      })
      .catch(() => vivo && setPartes([]));
    return () => {
      vivo = false;
    };
  }, [casoId]);

  if (!partes || partes.length === 0) return null;
  const p = DA_PESQUISA[servico];
  const daParte = (id: string) =>
    registradas.find((r) => r.investigadoId === id && r.servico === servico) ?? null;

  async function registrar(investigadoId: string, achou: boolean) {
    setIndo(investigadoId);
    setErro(null);
    try {
      const { registrada } = await apiPesquisasRegistradas.registrar(investigadoId, servico, achou);
      setRegistradas((rs) => [
        ...rs.filter((r) => !(r.investigadoId === investigadoId && r.servico === servico)),
        registrada,
      ]);
      aoMudar?.();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui registrar.');
    } finally {
      setIndo(null);
    }
  }

  async function apagar(r: PesquisaRegistrada) {
    setIndo(r.investigadoId);
    setErro(null);
    try {
      await apiPesquisasRegistradas.apagar(r.investigadoId, r.id);
      setRegistradas((rs) => rs.filter((x) => x.id !== r.id));
      aoMudar?.();
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui apagar o registro.');
    } finally {
      setIndo(null);
    }
  }

  const feitas = partes.filter((i) => daParte(i.id)).length;
  const acharam = partes.filter((i) => daParte(i.id)?.achou).length;

  /*
   * ⭐ **No desenho de Imóveis desde 19/09/2026 (PRD §46.7).** Era um bloco
   * sempre aberto, com todas as partes; agora é uma faixa com o estado — quantas
   * partes têm o registro — e "Registrei esta pesquisa" abre a lista, como em
   * Imóveis. ⛔ Muda o desenho, não o que o botão faz: as mesmas rotas, e só o
   * master registra. A faixa fica sempre à vista: é ela que diz se se olhou.
   */
  return (
    <div className="imv imv-pesquisa-fora">
      <div className="imv-fontes">
        <span className="imv-rot" title={`Pesquisa feita fora do Hub · ${p.titulo}`}>
          {p.rotuloCurto}
        </span>
        <span className={`imv-fonte${feitas === partes.length ? ' ok' : ' falta'}`}>
          <i />
          {feitas} de {partes.length} {partes.length === 1 ? 'parte registrada' : 'partes registradas'}
          {acharam > 0 && ` · ${p.achou.toLocaleLowerCase('pt-BR')} em ${acharam}`}
        </span>
        <span className="imv-esp" />
        <button
          type="button"
          className={`imv-bt fantasma p${aberto ? ' aberto' : ''}`}
          aria-expanded={aberto}
          onClick={() => setAberto(!aberto)}
        >
          <Traco d={['M5 4h14v16H5z', 'M8.5 8.5h7', 'M8.5 12h7', 'm8.5 15.5 1.5 1.5 3-3']} />
          Registrei esta pesquisa
        </button>
      </div>

      {aberto && (
        <div className="imv-painel">
          <p className="imv-painel-titulo">Pesquisa feita fora do Hub · {p.titulo}</p>
          <p className="ajuda">
            Registre o resultado de cada parte. É o registro que faz o escopo citar esta pesquisa e a
            ressalva dela sair no relatório — sem ele, o capítulo vazio parece dizer que ninguém olhou.
            O que se achou continua sendo lançado no caso, como hoje.
          </p>
          <ul className="imv-registros">
            {partes.map((i) => {
              const r = daParte(i.id);
              return (
                <li key={i.id}>
                  <span className="imv-registro-nome">{i.nome}</span>
                  <span className={`imv-registro-situacao${r ? ' feito' : ''}`}>
                    {r
                      ? `${r.achou ? p.achou : 'Nada consta'} · registrado em ${dia(r.em)}`
                      : 'Ainda não registrada'}
                  </span>
                  {ehMaster && (
                    <span className="imv-registro-acoes">
                      <button
                        type="button"
                        className="imv-bt p"
                        aria-pressed={r !== null && !r.achou}
                        disabled={indo === i.id || (r !== null && !r.achou)}
                        onClick={() => void registrar(i.id, false)}
                      >
                        Nada consta
                      </button>
                      <button
                        type="button"
                        className="imv-bt p"
                        aria-pressed={r !== null && r.achou}
                        disabled={indo === i.id || (r !== null && r.achou)}
                        onClick={() => void registrar(i.id, true)}
                      >
                        {p.achou}
                      </button>
                      {r && (
                        <button
                          type="button"
                          className="imv-bt p fantasma"
                          disabled={indo === i.id}
                          onClick={() => setAApagar({ registro: r, nome: i.nome })}
                        >
                          Apagar registro
                        </button>
                      )}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          {erro && <p className="erro">{erro}</p>}
        </div>
      )}

      {/* ⭐ **Pergunta antes — 19/09/2026.** Apagava com um clique, e o registro
          é o que põe a pesquisa no escopo e a ressalva no relatório. */}
      {aApagar && (
        <Confirmar
          titulo="Apagar este registro?"
          descricao={`Esta pesquisa volta a "ainda não registrada" para ${aApagar.nome}, e o relatório deixa de contá-la como feita para essa parte. O que foi lançado no caso continua.`}
          rotuloAcao="Apagar o registro"
          aoConfirmar={() => void apagar(aApagar.registro)}
          aoFechar={() => setAApagar(null)}
        />
      )}
    </div>
  );
}
